import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  BLUE_LIGHT,
  BLUE_POWDER_LIGHT,
  GRAY_DARKER,
  GRAY_LIGHT,
  PURPLE_DARK,
  PURPLE_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {omit} from 'util/omitKey';

import {Cell, CellProps, simpleTableStyles} from './SimpleTable';

// Ensure popdown aligns with bottom of header
const POPDOWN_OFFSET = 14;

export enum IconVisibility {
  ALWAYS = 'ALWAYS',
  ON_HOVER = 'ON_HOVER',
  HIDDEN = 'HIDDEN',
}

type IconColor = typeof BLUE_LIGHT | typeof PURPLE_DARK | typeof GRAY_DARKER;

const ICON_COLOR_TO_BACKGROUND_COLOR: Record<IconColor, string> = {
  [BLUE_LIGHT]: BLUE_POWDER_LIGHT,
  [PURPLE_DARK]: PURPLE_LIGHTEST,
  [GRAY_DARKER]: GRAY_LIGHT,
};

function getIconColorStyles(
  visibility: IconVisibility,
  isHovered: boolean,
  isDisabled: boolean,
  color: IconColor
): {color: string; background?: string; boxShadow?: string; border?: string} {
  if (isDisabled) {
    return {color: GRAY_DARKER};
  }

  switch (visibility) {
    case IconVisibility.ALWAYS:
      return {
        color,
        background: ICON_COLOR_TO_BACKGROUND_COLOR[color],
        boxShadow: `0px 0px 1px ${color}`,
        border: `1px solid ${color}`,
      };
    case IconVisibility.ON_HOVER:
      return isHovered ? {color: GRAY_DARKER} : {color: 'transparent'};
    case IconVisibility.HIDDEN:
      return {color: 'transparent'};
    default:
      assert(false, `Unexpected icon visibility mode ${visibility}`);
  }
}

export interface IconInfo {
  icon: BenchlingIconName;
  onClick: (() => void) | null;
  visibility: IconVisibility;
  isDisabled?: boolean;
  tooltipText?: string;
  color?: IconColor;
}

export interface PopdownInfo {
  renderContents: () => JSX.Element | null;
  /**
   * Whether to render the pop down contents.
   */
  isOpen: boolean;
  /**
   * Called when the user takes an action to close the popdown window, such as by clicking outside the
   * floating content or hitting the escape key.
   */
  onRequestClose?: () => void;
}

export type InteractiveHeaderCellProps = Omit<CellProps, 'hoverProps'> & {
  /**
   * Specifies the icons to show in the header cell.
   */
  iconInfos: Array<IconInfo>;
  /**
   * Specifies floating content to pop down from the header cell.
   */
  popdownInfo?: PopdownInfo;
};

/**
 * A header cell that can be used to add interactive functionality to column headers in <SimpleTable>,
 * including icons and interactive content that pops down from the column header.
 *
 * Icons may always show (IconVisibility.ALWAYS) or only show when the header is hovered
 * (IconVisibility.ON_HOVER). To reserve space for an icon that is sometimes shown, use IconVisibility.HIDDEN.
 *
 * All icons must be unique.
 */
export default function InteractiveHeaderCell(props: InteractiveHeaderCellProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();

  return (
    <Cell
      {...omit(props, 'iconInfos', 'children')}
      className={classNames(css(simpleTableStyles.grayHoverHeaderCell), props.className)}
      hoverProps={hoverProps}
    >
      <Float
        isOpen={props.popdownInfo?.isOpen ?? false}
        onRequestClose={props.popdownInfo?.onRequestClose}
        interaction={FloatingInteraction.CONTROLLED}
        placement={FloatingPlacement.BOTTOM_END}
        floatingElementOptions={{
          floatingBodyZIndex: ZINDEX_MODAL,
          floatingBodyClassName: css(styles.popdownBody),
          // Allow clicks to propagate, so popout controls like `DatePicker` can be embedded.
          eventPropagationOptions: {permitOnMouseDown: true},
        }}
        placementOptions={{offsetOverride: POPDOWN_OFFSET}}
        renderContent={props.popdownInfo?.renderContents ?? noop}
      >
        <div
          className="u-lr-split"
          {...getTestProps({
            component: 'InteractiveHeaderCell',
            element: 'floatToggle',
          })}
        >
          {props.children}
          <div>
            {props.iconInfos.map(({icon, onClick, visibility, isDisabled, color}) => (
              <BenchlingIcon
                key={icon}
                icon={icon}
                className={classNames(
                  visibility === IconVisibility.HIDDEN || isDisabled ? null : 'u-pointer',
                  css(styles.icon)
                )}
                style={getIconColorStyles(visibility, isHovered, isDisabled ?? false, color ?? BLUE_LIGHT)}
                onMouseDown={(e) => {
                  // Prevent highlighting content when clicking multiple times consecutively.
                  e.preventDefault();
                }}
                onClick={(event) => {
                  if (!isDisabled && visibility !== IconVisibility.HIDDEN && onClick != null) {
                    event.stopPropagation();
                    onClick();
                  }
                }}
              />
            ))}
          </div>
        </div>
      </Float>
    </Cell>
  );
}

const styles = StyleSheet.create({
  icon: {
    borderRadius: 5,
    height: 12,
    width: 12,
    marginLeft: 6,
    padding: 4,
  },
  popdownBody: {
    background: WHITE,
    borderRadius: 4,
    boxShadow: `0px 2px 4px ${GRAY_DARKER}`,
  },
});
